import React from 'react';
import SingletonRouter, { Router } from 'next/router';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import * as modalActions from 'state/modal/actions';
import getDisplayName from 'utils/getDisplayName';

const withLoseChangesWarning = (WrappedComponent) => {
  class WithLoseChangesWarning extends React.Component {
    static getInitialProps = context => (
      WrappedComponent.getInitialProps ? WrappedComponent.getInitialProps(context) : {}
    );

    static pageTitle = WrappedComponent.pageTitle;

    static displayName = `withLoseChangesWarning(${getDisplayName(WrappedComponent)})`;

    static propTypes = {
      dirty: PropTypes.bool.isRequired,
      hideModal: PropTypes.func.isRequired,
      showModal: PropTypes.func.isRequired,
      isSubmitting: PropTypes.bool,
      isCancelling: PropTypes.bool,
    };

    static defaultProps = {
      isSubmitting: undefined,
      isCancelling: undefined,
    };

    state = {
      routerArgs: null,
    }

    componentDidMount() {
      window.addEventListener('beforeunload', this.showBrowserWarning);
      // dirty workaround https://github.com/vercel/next.js/issues/2476#issuecomment-612483261
      SingletonRouter.router.change = (...args) => {
        this.setState({ routerArgs: args });
        if (this.continueNavigation() || !document.hasFocus()) {
          return Router.prototype.change.apply(SingletonRouter.router, args);
        }

        this.showWarning();

        // eslint-disable-next-line no-promise-executor-return
        return new Promise((resolve) => resolve(false));
      };
    }

    componentWillUnmount() {
      window.removeEventListener('beforeunload', this.showBrowserWarning);
      delete SingletonRouter.router.change;
    }

    continueNavigation = () => {
      const { isSubmitting, dirty, isCancelling } = this.props;

      return isCancelling || isSubmitting || !dirty;
    }

    showBrowserWarning = (evt) => {
      const { isSubmitting, dirty } = this.props;

      if (isSubmitting || !dirty) return;

      evt.preventDefault();

      // eslint-disable-next-line no-param-reassign
      evt.returnValue = '';
    }

    showWarning = () => {
      const { showModal } = this.props;

      showModal({
        modalType: 'CONFIRM_MODAL',
        modalProps: {
          title: { id: 'shared.discardChanges' },
          bodyText: [{ id: 'shared.cancelText' }],
          submitText: { id: 'shared.discard' },
          submitClassName: 'main-btn--danger',
          dismissText: { id: 'shared.cancel' },
          onSubmit: this.handleConfirmDiscard,
        },
      });
    }

    handleConfirmDiscard = () => {
      const { hideModal } = this.props;

      hideModal();
      Router.prototype.change.apply(SingletonRouter.router, this.state.routerArgs);
    };

    render() {
      return (
        <WrappedComponent {...this.props} />
      );
    }
  }

  const mapDispatchToProps = modalActions;

  return connect(null, mapDispatchToProps)(WithLoseChangesWarning);
};

export default withLoseChangesWarning;
